/**
 * useTools — fetch the user-facing tool registry for the capability rail.
 *
 * Mirrors `useSkills` and `usePersonas`: cache indefinitely, rely on
 * explicit invalidation when tools change. Tools are server-defined and
 * change rarely (only via migration), so a single fetch per session is
 * the right shape. Returns the GET /api/v4/tools array verbatim, parsed
 * through `fetchJson(apiFetch, ToolListSchema, …)` so a server shape change
 * surfaces as a logged warning instead of a downstream undefined-property
 * crash.
 */
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { ToolListSchema, type ToolListItem } from '@shared/schemas/tools';

export function useTools() {
  const { apiFetch } = useApi();
  return useQuery<ToolListItem[]>({
    queryKey: ['tools'],
    queryFn: async () => {
      return fetchJson(apiFetch, ToolListSchema, '/api/v4/tools');
    },
    staleTime: Infinity,
  });
}
